import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { workRequestAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const TITLE_MAP = {
  regularization: 'Attendance Regularization',
  wfh: 'Work From Home',
  outstation: 'Outstation Duty',
};

const buildApi = (requestType) => {
  if (requestType === 'regularization') {
    return {
      apply: workRequestAPI.applyRegularization,
      my: workRequestAPI.getMyRegularizations,
      team: workRequestAPI.getTeamRegularizations,
      approve: workRequestAPI.approveRegularization,
      reject: workRequestAPI.rejectRegularization,
    };
  }
  if (requestType === 'wfh') {
    return {
      apply: workRequestAPI.applyWfh,
      my: workRequestAPI.getMyWfh,
      team: workRequestAPI.getTeamWfh,
      approve: workRequestAPI.approveWfh,
      reject: workRequestAPI.rejectWfh,
    };
  }
  return {
    apply: workRequestAPI.applyOutstation,
    my: workRequestAPI.getMyOutstation,
    team: workRequestAPI.getTeamOutstation,
    approve: workRequestAPI.approveOutstation,
    reject: workRequestAPI.rejectOutstation,
  };
};

const WorkRequestModule = ({ requestType, mode = 'employee' }) => {
  const api = useMemo(() => buildApi(requestType), [requestType]);
  const [myRequests, setMyRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    attendance_date: '',
    requested_check_in: '',
    requested_check_out: '',
    from_date: '',
    to_date: '',
    reason: '',
    work_location: '',
    work_plan: '',
    destination: '',
    purpose: '',
    travel_mode: '',
    contact_details: '',
  });

  const canManageTeam = mode === 'manager' || mode === 'admin';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [myRes, teamRes] = await Promise.all([
        api.my(),
        canManageTeam ? api.team({ status: 'pending' }) : Promise.resolve({ data: [] }),
      ]);
      setMyRequests(myRes.data || []);
      setTeamRequests(teamRes.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load requests'));
    } finally {
      setLoading(false);
    }
  }, [api, canManageTeam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      let payload = {};
      if (requestType === 'regularization') {
        payload = {
          attendance_date: form.attendance_date,
          requested_check_in: form.requested_check_in || null,
          requested_check_out: form.requested_check_out || null,
          reason: form.reason,
        };
      } else if (requestType === 'wfh') {
        payload = {
          from_date: form.from_date,
          to_date: form.to_date,
          reason: form.reason,
          work_location: form.work_location,
          work_plan: form.work_plan,
        };
      } else {
        payload = {
          from_date: form.from_date,
          to_date: form.to_date,
          destination: form.destination,
          purpose: form.purpose,
          travel_mode: form.travel_mode,
          contact_details: form.contact_details,
          reason: form.reason,
        };
      }
      await api.apply(payload);
      toast.success(`${TITLE_MAP[requestType]} request submitted`);
      setForm({
        attendance_date: '',
        requested_check_in: '',
        requested_check_out: '',
        from_date: '',
        to_date: '',
        reason: '',
        work_location: '',
        work_plan: '',
        destination: '',
        purpose: '',
        travel_mode: '',
        contact_details: '',
      });
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit request'));
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.approve(id, {});
      toast.success('Request approved');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve request'));
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Enter rejection reason');
    if (!reason) return;
    try {
      await api.reject(id, { rejection_reason: reason });
      toast.success('Request rejected');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject request'));
    }
  };

  const getStatusVariant = (status) => {
    const key = String(status || '').toLowerCase();
    if (key === 'approved') return 'success';
    if (key === 'rejected') return 'destructive';
    if (key === 'pending_level2') return 'secondary';
    return 'warning';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{TITLE_MAP[requestType]} - Apply</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleApply}>
            {requestType === 'regularization' && (
              <div className="grid md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Attendance Date</Label>
                  <Input type="date" value={form.attendance_date} onChange={(e) => setForm({ ...form, attendance_date: e.target.value })} required />
                </div>
                <div className="space-y-1">
                  <Label>Requested Check-in</Label>
                  <Input type="datetime-local" value={form.requested_check_in} onChange={(e) => setForm({ ...form, requested_check_in: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Requested Check-out</Label>
                  <Input type="datetime-local" value={form.requested_check_out} onChange={(e) => setForm({ ...form, requested_check_out: e.target.value })} />
                </div>
              </div>
            )}

            {(requestType === 'wfh' || requestType === 'outstation') && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>From Date</Label>
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} required />
                </div>
                <div className="space-y-1">
                  <Label>To Date</Label>
                  <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} required />
                </div>
              </div>
            )}

            {requestType === 'wfh' && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Work Location</Label>
                  <Input value={form.work_location} onChange={(e) => setForm({ ...form, work_location: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Work Plan</Label>
                  <Input value={form.work_plan} onChange={(e) => setForm({ ...form, work_plan: e.target.value })} />
                </div>
              </div>
            )}

            {requestType === 'outstation' && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Destination</Label>
                  <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} required />
                </div>
                <div className="space-y-1">
                  <Label>Travel Mode</Label>
                  <Input value={form.travel_mode} onChange={(e) => setForm({ ...form, travel_mode: e.target.value })} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Purpose</Label>
                  <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} required />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Contact Details</Label>
                  <Input value={form.contact_details} onChange={(e) => setForm({ ...form, contact_details: e.target.value })} />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required />
            </div>

            <Button type="submit" disabled={loading}>Submit Request</Button>
          </form>
        </CardContent>
      </Card>

      {canManageTeam && (
        <Card>
          <CardHeader>
            <CardTitle>{TITLE_MAP[requestType]} - Team Queue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {teamRequests.map((item) => (
              <div key={item.id} className="p-3 border rounded-md flex justify-between items-start">
                <div>
                  <p className="font-medium">{item.employee_name}</p>
                  <p className="text-sm text-muted-foreground">{item.reason || '-'}</p>
                  <Badge variant={getStatusVariant(item.status)} className="mt-2">{item.status}</Badge>
                </div>
                {(item.status === 'pending' || item.status === 'pending_level2') && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleApprove(item.id)}>Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => handleReject(item.id)}>Reject</Button>
                  </div>
                )}
              </div>
            ))}
            {teamRequests.length === 0 && (
              <p className="text-sm text-muted-foreground">No pending team requests.</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{TITLE_MAP[requestType]} - My Requests</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {myRequests.map((item) => (
            <div key={item.id} className="p-3 border rounded-md flex justify-between items-center">
              <div>
                <p className="font-medium">Request #{item.id}</p>
                <p className="text-sm text-muted-foreground">{item.reason || '-'}</p>
              </div>
              <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>
            </div>
          ))}
          {myRequests.length === 0 && (
            <p className="text-sm text-muted-foreground">No requests yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkRequestModule;
